/*
 * Copyright 2026 North Carolina State University
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  TextField,
  Grid,
  IconButton,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Card,
  CardContent,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Checkbox,
  OutlinedInput,
  ListItemText
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import AddIcon from '@mui/icons-material/Add';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { toast } from 'react-hot-toast';
import ResourceAssignDialog from './ResourceAssignDialog';
import GenerateNewInvoice from './GenerateNewInvoice';
import EditInvoice from './EditInvoice';
import RecordPayment from './RecordPayment';
import EditPayment from './EditPayment';
import InvoiceDetail from './InvoiceDetail';
import useFacility from '../../hooks/useFacility';

const getInvoiceNumber = (invoiceId) => {
  if (!invoiceId) return 'N/A';
  const parts = String(invoiceId).split('_INV');
  if (parts.length < 2) return 'N/A';
  const num = parseInt(parts[1], 10);
  return isNaN(num) ? 'N/A' : num;
};

const ResourceInvoicing = () => {
  const { facilityPreference, loading: prefLoading } = useFacility();

  const [projects, setProjects] = useState([]);
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFacility, setSelectedFacility] = useState('all');
  // Seeded from ?search= when an invoice notification deep-links here, so the
  // project the notification is about is already filtered for.
  const location = useLocation();
  const searchFromUrl = new URLSearchParams(location.search).get('search');
  const [searchTerm, setSearchTerm] = useState(searchFromUrl || '');
  useEffect(() => {
    if (searchFromUrl) setSearchTerm(searchFromUrl);
  }, [searchFromUrl]);
  const [resourceBillingFilter, setResourceBillingFilter] = useState('all');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [projectStatusFilter, setProjectStatusFilter] = useState(['Approved', 'In Progress', 'Completed']);

  // Resource assign dialog
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedProjectForAssign, setSelectedProjectForAssign] = useState(null);

  // Invoice generation dialog
  const [generateInvoiceDialogOpen, setGenerateInvoiceDialogOpen] = useState(false);
  const [selectedProjectForInvoice, setSelectedProjectForInvoice] = useState(null);

  // Invoices per project
  const [projectInvoices, setProjectInvoices] = useState({});

  // Invoice detail
  const [showInvoiceDetail, setShowInvoiceDetail] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  // Delete invoice
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState(null);
  const [deletionPreview, setDeletionPreview] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Edit invoice
  const [editInvoiceDialogOpen, setEditInvoiceDialogOpen] = useState(false);
  const [invoiceToEdit, setInvoiceToEdit] = useState(null);
  const [projectForEdit, setProjectForEdit] = useState(null);

  // Unified payments dialog
  const [paymentsDialogOpen, setPaymentsDialogOpen] = useState(false);
  const [paymentsInvoice, setPaymentsInvoice] = useState(null);
  const [showRecordPayment, setShowRecordPayment] = useState(false);
  const [editPaymentDialogOpen, setEditPaymentDialogOpen] = useState(false);
  const [paymentToEdit, setPaymentToEdit] = useState(null);
  const [deletePaymentConfirmOpen, setDeletePaymentConfirmOpen] = useState(false);
  const [paymentToDelete, setPaymentToDelete] = useState(null);
  const [isDeletingPayment, setIsDeletingPayment] = useState(false);

  // Account mapping dialog
  const [accountMappingDialogOpen, setAccountMappingDialogOpen] = useState(false);
  const [invoiceForMapping, setInvoiceForMapping] = useState(null);
  const [projectForMapping, setProjectForMapping] = useState(null);
  const [selectedMappingFacility, setSelectedMappingFacility] = useState('');
  const [selectedFacilityAccounts, setSelectedFacilityAccounts] = useState([]);
  const [selectedAccountNumber, setSelectedAccountNumber] = useState('');

  useEffect(() => {
    if (prefLoading) return;
    // A notification deep link must be able to find its project whatever
    // facility it belongs to, so it wins over the admin's default facility.
    if (searchFromUrl) {
      setSelectedFacility('all');
    } else if (facilityPreference) {
      setSelectedFacility(facilityPreference);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [facilityPreference, prefLoading, searchFromUrl]);

  const fetchFacilities = async () => {
    try {
      const res = await axios.get('/api/facilities');
      setFacilities(res.data);
    } catch {
      toast.error('Failed to fetch facilities');
    }
  };

  const fetchProjects = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append('application_type', 'resource_only');
      params.append('limit', '1000');
      if (selectedFacility !== 'all') params.append('facility', selectedFacility);
      if (projectStatusFilter.length > 0) params.append('status', projectStatusFilter.join(','));

      const res = await axios.get(`/api/admin/projects?${params.toString()}`);
      const projectsData = res.data.projects || res.data || [];
      const projectsArr = Array.isArray(projectsData) ? projectsData : [];

      const invoicesData = {};
      for (const project of projectsArr) {
        if (project._id) {
          try {
            const invRes = await axios.get(`/api/invoices/by-project/${project._id}`);
            invoicesData[project._id] = invRes.data || [];
          } catch {
            invoicesData[project._id] = [];
          }
        }
      }
      setProjects(projectsArr);
      setProjectInvoices(invoicesData);
    } catch {
      toast.error('Failed to fetch resource-only projects');
    } finally {
      setLoading(false);
    }
  }, [selectedFacility, projectStatusFilter]);

  useEffect(() => { fetchFacilities(); }, []);

  // Wait for facility preference before the first fetch so we don't briefly
  // show projects from all facilities then snap to the preferred one.
  useEffect(() => {
    if (prefLoading) return;
    fetchProjects();
  }, [prefLoading, fetchProjects]);

  const getPaymentStatus = (invoice) => {
    const total = invoice.total_amount ?? invoice.current_period_amount ?? 0;
    const paid = invoice.amount_paid ?? 0;
    const remaining = invoice.remaining_balance ?? (total - paid);
    if (remaining <= 0 || paid >= total) return 'paid';
    if (paid > 0) return 'partially_paid';
    return 'unpaid';
  };

  const getUnbilledQty = (ra) => {
    const invoicedQty = (ra.invoiced_quantities || []).reduce((sum, iq) => sum + (iq.quantity || 0), 0);
    return Math.max(0, (ra.quantity || 0) - invoicedQty);
  };

  const getInvoiceStatus = (invoice) => {
    if (!invoice.status_history || invoice.status_history.length === 0) return 'Unknown';
    const sorted = [...invoice.status_history].sort((a, b) => new Date(b.date) - new Date(a.date));
    return sorted[0].status;
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'Paid': return 'success';
      case 'Partially Paid': return 'warning';
      case 'Overdue': return 'error';
      case 'Sent to Client': return 'info';
      default: return 'default';
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const d = new Date(dateString);
    return `${d.getUTCMonth() + 1}/${d.getUTCDate()}/${d.getUTCFullYear()}`;
  };

  const formatCurrency = (val) => `$${(val || 0).toFixed(2)}`;

  const formatRateBasis = (basis) => {
    if (!basis) return '/ unit';
    return `/ ${basis}`;
  };

  const filteredProjects = projects.filter(p => {
    if (p.application_type !== 'resource_only') return false;

    const term = searchTerm?.toLowerCase() || '';
    const pi = p.users?.[0];
    const matchesSearch = !term || (
      p.project_id?.toLowerCase().includes(term) ||
      p.project_title?.toLowerCase().includes(term) ||
      (pi && `${pi.first_name} ${pi.last_name}`.toLowerCase().includes(term)) ||
      (pi && pi.email?.toLowerCase().includes(term))
    );
    if (!matchesSearch) return false;

    const assignments = p.resource_assignments || [];
    const invoicesList = projectInvoices[p._id] || [];
    const hasUnbilled = assignments.some(ra => getUnbilledQty(ra) > 0);
    const isBilled = invoicesList.length > 0 && !hasUnbilled;
    const matchesResourceBilling =
      resourceBillingFilter === 'all' ||
      (resourceBillingFilter === 'unbilled' && (hasUnbilled || assignments.length === 0)) ||
      (resourceBillingFilter === 'billed' && isBilled);

    const matchesPaymentStatus =
      paymentStatusFilter === 'all' ||
      (invoicesList.length > 0 && invoicesList.some(inv => getPaymentStatus(inv) === paymentStatusFilter));

    return matchesSearch && matchesResourceBilling && matchesPaymentStatus;
  });

  // --- Handlers ---

  const handleOpenAssignDialog = (project) => {
    setSelectedProjectForAssign(project);
    setAssignDialogOpen(true);
  };

  const handleOpenInvoiceDialog = async (project) => {
    try {
      const response = await axios.get(`/api/projects/${project._id}`);
      setSelectedProjectForInvoice(response.data);
      setGenerateInvoiceDialogOpen(true);
    } catch {
      toast.error('Failed to load project details');
      setSelectedProjectForInvoice(project);
      setGenerateInvoiceDialogOpen(true);
    }
  };

  const handleInvoiceCreated = () => {
    fetchProjects();
    toast.success('Invoice generated successfully');
  };

  const handleViewPDF = async (invoice) => {
    try {
      const response = await axios.get(`/api/invoices/${invoice._id}/pdf?invoice_id=${invoice.invoice_id}`, { responseType: 'blob' });
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch {
      toast.error('Failed to view PDF');
    }
  };

  const handleDownloadPDF = async (invoiceId, invoice_id) => {
    try {
      let id = invoice_id;
      if (!id) {
        const invoice = Object.values(projectInvoices).flat().find(inv => inv._id === invoiceId || inv.parentId === invoiceId);
        if (invoice) id = invoice.invoice_id;
        else { toast.error('Missing invoice ID'); return; }
      }
      const response = await axios.get(`/api/invoices/${invoiceId}/pdf?invoice_id=${id}`, { responseType: 'blob' });
      let filename = `invoice-${getInvoiceNumber(id)}.pdf`;
      const cd = response.headers['content-disposition'];
      if (cd) {
        const m = cd.match(/filename[^;=\n]*=(?:(?:"([^"]*)")|([^;\n]*))/i);
        if (m && (m[1] || m[2])) filename = m[1] || m[2];
      }
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      toast.success('PDF downloaded successfully');
    } catch {
      toast.error('Failed to download PDF');
    }
  };

  const handleEditInvoice = (invoice, project) => {
    setInvoiceToEdit(invoice);
    setProjectForEdit(project);
    setEditInvoiceDialogOpen(true);
  };

  const handleInvoiceUpdated = () => {
    fetchProjects();
    setEditInvoiceDialogOpen(false);
    setInvoiceToEdit(null);
    setProjectForEdit(null);
  };

  const handleShowInvoiceDetail = (invoice) => {
    setSelectedInvoice(invoice);
    setShowInvoiceDetail(true);
  };

  // Unified payments dialog
  const handleOpenPayments = async (invoice) => {
    try {
      const response = await axios.get(
        `/api/invoices/${invoice._id || invoice.parentId}?populate=true&invoice_id=${invoice.invoice_id}`
      );
      setPaymentsInvoice(response.data);
      setPaymentsDialogOpen(true);
    } catch {
      setPaymentsInvoice(invoice);
      setPaymentsDialogOpen(true);
    }
  };

  const handlePaymentRecorded = async () => {
    fetchProjects();
    setShowRecordPayment(false);
    if (paymentsInvoice) {
      try {
        const response = await axios.get(
          `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}?populate=true&invoice_id=${paymentsInvoice.invoice_id}`
        );
        setPaymentsInvoice(response.data);
      } catch {
        // silent
      }
    }
  };

  const handlePaymentUpdated = async () => {
    fetchProjects();
    setEditPaymentDialogOpen(false);
    setPaymentToEdit(null);
    if (paymentsInvoice) {
      try {
        const response = await axios.get(
          `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}?populate=true&invoice_id=${paymentsInvoice.invoice_id}`
        );
        setPaymentsInvoice(response.data);
      } catch {
        // silent
      }
    }
  };

  const handleConfirmDeletePayment = async () => {
    if (!paymentsInvoice || !paymentToDelete) return;
    setIsDeletingPayment(true);
    try {
      await axios.delete(
        `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}/payment/${paymentToDelete._id}?invoice_id=${paymentsInvoice.invoice_id}`
      );
      toast.success('Payment deleted successfully');
      setDeletePaymentConfirmOpen(false);
      setPaymentToDelete(null);
      fetchProjects();
      if (paymentsInvoice) {
        try {
          const response = await axios.get(
            `/api/invoices/${paymentsInvoice._id || paymentsInvoice.parentId}?populate=true&invoice_id=${paymentsInvoice.invoice_id}`
          );
          setPaymentsInvoice(response.data);
        } catch {
          // silent
        }
      }
    } catch (error) {
      console.error('Error deleting payment:', error);
      toast.error('Failed to delete payment: ' + (error.response?.data?.message || error.message));
    } finally {
      setIsDeletingPayment(false);
    }
  };

  // Account mapping
  const getAccountMappingInfo = (invoice) => {
    if (!invoice?.account_mapping?.facility_id || !invoice?.account_mapping?.account_number) {
      return null;
    }
    const facilityId = typeof invoice.account_mapping.facility_id === 'object'
      ? invoice.account_mapping.facility_id._id || invoice.account_mapping.facility_id
      : invoice.account_mapping.facility_id;
    const facility = facilities.find(f => f._id === facilityId);
    if (!facility) return null;
    return {
      facilityName: facility.facility_name,
      accountNumber: invoice.account_mapping.account_number
    };
  };

  const handleOpenAccountMapping = (invoice, project) => {
    setInvoiceForMapping(invoice);
    setProjectForMapping(project);
    if (invoice?.account_mapping?.facility_id) {
      const facilityId = typeof invoice.account_mapping.facility_id === 'object'
        ? invoice.account_mapping.facility_id._id || invoice.account_mapping.facility_id
        : invoice.account_mapping.facility_id;
      setSelectedMappingFacility(facilityId);
      const facility = facilities.find(f => f._id === facilityId);
      if (facility) {
        setSelectedFacilityAccounts(facility.accounts_receivable || []);
        setSelectedAccountNumber(invoice.account_mapping.account_number || '');
      }
    } else {
      setSelectedMappingFacility('');
      setSelectedFacilityAccounts([]);
      setSelectedAccountNumber('');
    }
    setAccountMappingDialogOpen(true);
  };

  const handleMappingFacilityChange = (facilityId) => {
    setSelectedMappingFacility(facilityId);
    const facility = facilities.find(f => f._id === facilityId);
    if (facility) {
      setSelectedFacilityAccounts(facility.accounts_receivable || []);
    } else {
      setSelectedFacilityAccounts([]);
    }
    setSelectedAccountNumber('');
  };

  const handleSaveAccountMapping = async () => {
    try {
      if (!invoiceForMapping || !projectForMapping) {
        toast.error('Missing invoice or project information');
        return;
      }
      const response = await axios.patch(
        `/api/invoices/${projectForMapping.project_id || projectForMapping._id}/${invoiceForMapping.invoice_id}/account-mapping`,
        {
          facility_id: selectedMappingFacility || null,
          account_number: selectedAccountNumber || null
        }
      );
      if (response.data.success) {
        toast.success('Account mapping updated successfully');
        fetchProjects();
        setAccountMappingDialogOpen(false);
      } else {
        toast.error('Failed to update account mapping');
      }
    } catch {
      toast.error('Failed to update account mapping');
    }
  };

  // Delete invoice
  const handleDeleteInvoice = async (invoice, project) => {
    try {
      const previewResponse = await axios.get(`/api/invoices/deletion-preview/${project.project_id}/${invoice.invoice_id}`);
      if (!previewResponse.data.success) {
        toast.error(previewResponse.data.message || 'Failed to get deletion preview');
        return;
      }
      setDeletionPreview(previewResponse.data.preview);
      setInvoiceToDelete({ invoice, project });
      setDeleteConfirmOpen(true);
    } catch (err) {
      toast.error('Failed to get deletion preview: ' + (err.response?.data?.message || err.message));
    }
  };

  const handleConfirmDelete = async () => {
    if (!invoiceToDelete) return;
    try {
      setIsDeleting(true);
      const { invoice, project } = invoiceToDelete;
      const response = await axios.delete(`/api/invoices/${project.project_id}/${invoice.invoice_id}`);
      if (response.data.success) {
        toast.success('Invoice deleted successfully');
        await fetchProjects();
        setDeleteConfirmOpen(false);
        setInvoiceToDelete(null);
        setDeletionPreview(null);
      } else {
        toast.error(response.data.message || 'Failed to delete invoice');
      }
    } catch (err) {
      toast.error('Failed to delete invoice: ' + (err.response?.data?.message || err.message));
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setDeleteConfirmOpen(false);
    setInvoiceToDelete(null);
    setDeletionPreview(null);
  };

  return (
    <div>
      <Typography variant="h4" gutterBottom sx={{ mb: 4, fontWeight: 500 }}>
        Resource Invoicing
      </Typography>

      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Manage resource-only requests: assign resources, generate invoices, record payments, and track billing.
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={3}>
            <TextField
              fullWidth
              size="small"
              label="Search Projects"
              placeholder="Search by ID, title, or requester name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Facility</InputLabel>
              <Select
                value={selectedFacility}
                onChange={(e) => setSelectedFacility(e.target.value)}
                label="Facility"
              >
                <MenuItem value="all">All Facilities</MenuItem>
                {facilities.map(f => (
                  <MenuItem key={f._id} value={f._id}>{f.facility_name}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Billing Status</InputLabel>
              <Select
                value={resourceBillingFilter}
                onChange={(e) => setResourceBillingFilter(e.target.value)}
                label="Billing Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="unbilled">Unbilled</MenuItem>
                <MenuItem value="billed">Billed</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment Status</InputLabel>
              <Select
                value={paymentStatusFilter}
                onChange={(e) => setPaymentStatusFilter(e.target.value)}
                label="Payment Status"
              >
                <MenuItem value="all">All</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="unpaid">Unpaid</MenuItem>
                <MenuItem value="partially_paid">Partially Paid</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Project Status</InputLabel>
              <Select
                multiple
                value={projectStatusFilter}
                onChange={(e) => setProjectStatusFilter(e.target.value)}
                input={<OutlinedInput label="Project Status" />}
                renderValue={(selected) => (selected.length === 3 ? 'All' : selected.join(', '))}
              >
                {['Approved', 'In Progress', 'Completed'].map((status) => (
                  <MenuItem key={status} value={status}>
                    <Checkbox checked={projectStatusFilter.indexOf(status) > -1} />
                    <ListItemText primary={status} />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Paper>

      {/* Projects List */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        {loading || prefLoading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 2, p: 6 }}>
            <CircularProgress />
            <Typography variant="body2" color="text.secondary">
              Loading projects and invoices...
            </Typography>
          </Box>
        ) : filteredProjects.length === 0 ? (
          <Paper sx={{ p: 3, textAlign: 'center' }}>
            <Typography>No resource-only projects found</Typography>
          </Paper>
        ) : (
          filteredProjects.map((project) => {
            const displayUser = project.users?.[0];
            const projectInvoicesList = projectInvoices[project._id] || [];
            const hasInvoices = projectInvoicesList.length > 0;
            const latestInvoice = hasInvoices ? projectInvoicesList[0] : null;
            const invoiceStatusLabel = latestInvoice ? getInvoiceStatus(latestInvoice) : 'Not Invoiced';

            return (
              <Accordion
                key={project._id}
                elevation={0}
                disableGutters
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                  overflow: 'hidden',
                  '&:not(:last-child)': { mb: 2 },
                  '&::before': { display: 'none' },
                  '&.Mui-expanded': {
                    margin: 0,
                    borderLeft: '4px solid',
                    borderLeftColor: 'primary.main',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
                    '& .MuiAccordionSummary-root': {
                      backgroundColor: 'action.selected',
                      borderBottom: '1px solid',
                      borderColor: 'divider',
                    },
                  },
                }}
              >
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon sx={{ color: 'primary.main' }} />}
                  sx={{
                    backgroundColor: 'background.paper',
                    minHeight: 56,
                    '&:hover': { backgroundColor: 'action.hover' },
                    '&.Mui-expanded': {
                      minHeight: 56,
                      backgroundColor: 'action.selected',
                    },
                  }}
                >
                  <Grid container alignItems="center" spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Typography variant="subtitle1" sx={{ fontWeight: 500 }}>
                        {project.project_id}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {project.project_title}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        <strong>Requester:</strong> {displayUser ? `${displayUser.first_name} ${displayUser.last_name}` : 'N/A'}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Facility:</strong> {project.facility?.facility_name || 'N/A'}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Typography variant="body2">
                        <strong>Status:</strong> {project.project_status}
                      </Typography>
                      <Typography variant="body2">
                        <strong>Assignments:</strong> {project.resource_assignments?.length || 0} | <strong>Invoices:</strong> {projectInvoicesList.length}
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Chip
                          label={invoiceStatusLabel}
                          size="small"
                          color={getStatusColor(invoiceStatusLabel)}
                        />
                        <Stack direction="row" spacing={0.5}>
                          <Button
                            variant="outlined"
                            size="small"
                            startIcon={<AddIcon />}
                            onClick={(e) => { e.stopPropagation(); handleOpenAssignDialog(project); }}
                            sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                          >
                            Assign
                          </Button>
                          {!hasInvoices && (
                            <Button
                              variant="contained"
                              size="small"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenInvoiceDialog(project);
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                            >
                              Generate Invoice
                            </Button>
                          )}
                        </Stack>
                      </Box>
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    backgroundColor: 'grey.50',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    py: 3,
                    px: 3,
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                    {/* Resource assignments table */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Resource Assignments</Typography>
                      <TableContainer component={Paper} variant="outlined">
                        <Table size="small">
                          <TableHead>
                            <TableRow>
                              <TableCell>Resource</TableCell>
                              <TableCell>Unit</TableCell>
                              <TableCell>Rate</TableCell>
                              <TableCell align="right">Total Qty</TableCell>
                              <TableCell align="right">Invoiced</TableCell>
                              <TableCell align="right">Unbilled</TableCell>
                              <TableCell align="right">Est. Cost</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(project.resource_assignments || []).length === 0 ? (
                              <TableRow>
                                <TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>
                                  No resources assigned yet. Use &quot;Assign&quot; to add resources.
                                </TableCell>
                              </TableRow>
                            ) : (
                              (project.resource_assignments || []).map((ra, i) => {
                                const resource = ra.resource;
                                const unit = resource?.unit;
                                const rates = unit?.rates || [];
                                const rateObj = rates.find(r => r.rate_id === ra.rate_id);
                                const rate = rateObj?.rate || 0;
                                const invoicedQty = (ra.invoiced_quantities || []).reduce((sum, iq) => sum + (iq.quantity || 0), 0);
                                const unbilledQty = Math.max(0, (ra.quantity || 0) - invoicedQty);
                                return (
                                  <TableRow key={ra._id || i}>
                                    <TableCell>{resource?.resource || 'Unknown'}</TableCell>
                                    <TableCell>{unit?.unit_short || unit?.base_unit || 'N/A'}</TableCell>
                                    <TableCell>
                                      {rateObj?.display_name && (
                                        <Chip size="small" label={rateObj.display_name} sx={{ mr: 0.5 }} />
                                      )}
                                      {formatCurrency(rate)} {formatRateBasis(rateObj?.rate_basis)}
                                    </TableCell>
                                    <TableCell align="right">{ra.quantity}</TableCell>
                                    <TableCell align="right">
                                      {invoicedQty > 0 ? (
                                        <Chip size="small" color="success" label={invoicedQty} />
                                      ) : '0'}
                                    </TableCell>
                                    <TableCell align="right">
                                      {unbilledQty > 0 ? (
                                        <Chip size="small" color="warning" label={unbilledQty} />
                                      ) : (
                                        <Chip size="small" color="default" label="0" />
                                      )}
                                    </TableCell>
                                    <TableCell align="right">{formatCurrency(unbilledQty * rate)}</TableCell>
                                  </TableRow>
                                );
                              })
                            )}
                          </TableBody>
                        </Table>
                      </TableContainer>
                    </Box>

                    {/* Invoices list */}
                    <Box>
                      <Typography variant="subtitle2" sx={{ mb: 1 }}>Invoices</Typography>
                      {hasInvoices ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                          {[...projectInvoicesList].sort((a, b) => {
                            const dateA = a.billing_period?.start_date || a.generation_date;
                            const dateB = b.billing_period?.start_date || b.generation_date;
                            return new Date(dateA) - new Date(dateB);
                          }).map((invoice, invoiceIndex) => (
                            <Card key={`${project._id}-${invoice._id}-${invoiceIndex}`} variant="outlined">
                              <CardContent>
                                <Grid container spacing={2} alignItems="center">
                                  {/* Column 1: Invoice #, Period, Generated */}
                                  <Grid item xs={12} md={3}>
                                    <Typography variant="subtitle1">
                                      Invoice #{getInvoiceNumber(invoice.invoice_id)}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Period: {invoice.billing_period
                                        ? `${formatDate(invoice.billing_period.start_date)} - ${formatDate(invoice.billing_period.end_date)}`
                                        : 'N/A'}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                      Generated: {formatDate(invoice.generation_date)}
                                    </Typography>
                                  </Grid>
                                  {/* Column 2: Amount, Status */}
                                  <Grid item xs={12} md={2}>
                                    <Typography variant="body2">
                                      <strong>Amount:</strong> ${(invoice.current_period_amount || invoice.total_amount)?.toFixed(2)}
                                    </Typography>
                                    <Typography variant="body2">
                                      <strong>Status:</strong> {getInvoiceStatus(invoice)}
                                    </Typography>
                                  </Grid>
                                  {/* Column 3: A/R Mapping */}
                                  <Grid item xs={12} md={3}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                      {(() => {
                                        const mappingInfo = getAccountMappingInfo(invoice);
                                        if (mappingInfo) {
                                          return (
                                            <>
                                              <LinkIcon sx={{ fontSize: 14, color: 'success.main' }} />
                                              <Typography variant="body2" sx={{ color: 'success.main' }}>
                                                <strong>A/R:</strong> {mappingInfo.facilityName} - {mappingInfo.accountNumber}
                                              </Typography>
                                              <IconButton aria-label="Edit A/R Mapping"
                                                size="small"
                                                sx={{ p: 0.25, ml: 0.5 }}
                                                onClick={() => handleOpenAccountMapping(invoice, project)}
                                                title="Edit A/R Mapping"
                                              >
                                                <EditIcon sx={{ fontSize: 14 }} />
                                              </IconButton>
                                            </>
                                          );
                                        } else {
                                          return (
                                            <>
                                              <LinkOffIcon sx={{ fontSize: 14, color: 'warning.main' }} />
                                              <Typography variant="body2" sx={{ color: 'warning.main' }}>
                                                <strong>A/R:</strong> Not mapped
                                              </Typography>
                                              <Button
                                                size="small"
                                                variant="text"
                                                sx={{ minWidth: 'auto', p: 0.25, fontSize: '0.7rem', ml: 0.5 }}
                                                onClick={() => handleOpenAccountMapping(invoice, project)}
                                              >
                                                Map
                                              </Button>
                                            </>
                                          );
                                        }
                                      })()}
                                    </Box>
                                  </Grid>
                                  {/* Column 4: Action Buttons */}
                                  <Grid item xs={12} md={4}>
                                    <Stack
                                      direction="row"
                                      spacing={0.75}
                                      justifyContent="flex-end"
                                      flexWrap="wrap"
                                      useFlexGap
                                      sx={{ rowGap: 0.75 }}
                                    >
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleEditInvoice(invoice, project)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                      >
                                        Edit Invoice
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleViewPDF(invoice)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                      >
                                        View PDF
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        onClick={() => handleDownloadPDF(invoice._id, invoice.invoice_id)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                      >
                                        Download PDF
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="success"
                                        onClick={() => handleOpenPayments(invoice)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                      >
                                        Payments{invoice.payments?.length > 0 ? ` (${invoice.payments.length})` : ''}
                                      </Button>
                                      <Button
                                        size="small"
                                        variant="outlined"
                                        color="error"
                                        onClick={() => handleDeleteInvoice(invoice, project)}
                                        sx={{ textTransform: 'none', fontSize: '0.75rem', py: 0.25, px: 1, minWidth: 0, borderRadius: 1 }}
                                      >
                                        Delete
                                      </Button>
                                    </Stack>
                                  </Grid>
                                </Grid>
                              </CardContent>
                            </Card>
                          ))}
                          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                            <Button
                              variant="outlined"
                              size="small"
                              onClick={() => handleOpenInvoiceDialog(project)}
                            >
                              Create New Invoice
                            </Button>
                          </Box>
                        </Box>
                      ) : (
                        <Typography color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                          No invoices generated for this project yet.
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </AccordionDetails>
              </Accordion>
            );
          })
        )}
      </Box>

      {/* Resource Assign Dialog */}
      <ResourceAssignDialog
        open={assignDialogOpen}
        onClose={() => { setAssignDialogOpen(false); fetchProjects(); }}
        project={selectedProjectForAssign}
        onSave={() => { fetchProjects(); }}
      />

      {/* Generate Invoice Dialog */}
      <GenerateNewInvoice
        open={generateInvoiceDialogOpen}
        onClose={() => {
          setGenerateInvoiceDialogOpen(false);
          setSelectedProjectForInvoice(null);
        }}
        selectedProject={selectedProjectForInvoice}
        onInvoiceCreated={handleInvoiceCreated}
        setSelectedProject={setSelectedProjectForInvoice}
        resourceOnly={true}
      />

      {/* Invoice Detail Dialog */}
      {showInvoiceDetail && selectedInvoice && (
        <InvoiceDetail
          open={showInvoiceDetail}
          onClose={() => { setShowInvoiceDetail(false); setSelectedInvoice(null); }}
          invoiceId={selectedInvoice._id || selectedInvoice.parentId}
          selectedInvoice={selectedInvoice}
          onUpdate={handlePaymentRecorded}
        />
      )}

      {/* Unified Payments Dialog */}
      <Dialog
        open={paymentsDialogOpen}
        onClose={() => {
          setPaymentsDialogOpen(false);
          setPaymentsInvoice(null);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              Payments — Invoice #{getInvoiceNumber(paymentsInvoice?.invoice_id)}
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <Typography variant="body2" color="text.secondary">
                Total: <strong>${paymentsInvoice?.total_amount?.toFixed(2) || '0.00'}</strong>
                {' | '}Paid: <strong>${paymentsInvoice?.amount_paid?.toFixed(2) || '0.00'}</strong>
                {' | '}Balance: <strong>${paymentsInvoice?.remaining_balance?.toFixed(2) ||
                  ((paymentsInvoice?.total_amount || 0) - (paymentsInvoice?.amount_paid || 0)).toFixed(2)}</strong>
              </Typography>
            </Box>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {paymentsInvoice?.payments?.length > 0 ? (
            <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ backgroundColor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Paid By</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Amount</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Mode</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Receipt / Txn ID</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Comments</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paymentsInvoice.payments.map((payment, idx) => {
                    const paidByName = typeof payment.paid_by === 'object' && payment.paid_by
                      ? `${payment.paid_by.first_name || ''} ${payment.paid_by.last_name || ''}`.trim() || 'N/A'
                      : 'N/A';
                    return (
                      <TableRow key={payment._id || idx} hover>
                        <TableCell>{new Date(payment.date).toLocaleDateString()}</TableCell>
                        <TableCell>{paidByName}</TableCell>
                        <TableCell>${payment.amount?.toFixed(2) || '0.00'}</TableCell>
                        <TableCell>{payment.payment_mode}</TableCell>
                        <TableCell>{payment.receipt_number || payment.transaction_id || '—'}</TableCell>
                        <TableCell>{payment.comments || '—'}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Button
                              size="small"
                              variant="text"
                              onClick={() => {
                                setPaymentToEdit(payment);
                                setEditPaymentDialogOpen(true);
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}
                            >
                              Edit
                            </Button>
                            <Button
                              size="small"
                              variant="text"
                              color="error"
                              onClick={() => {
                                setPaymentToDelete(payment);
                                setDeletePaymentConfirmOpen(true);
                              }}
                              sx={{ textTransform: 'none', fontSize: '0.75rem', minWidth: 0 }}
                            >
                              Delete
                            </Button>
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          ) : (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography color="text.secondary">No payments recorded yet.</Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setPaymentsDialogOpen(false);
              setPaymentsInvoice(null);
            }}
          >
            Close
          </Button>
          {paymentsInvoice && (paymentsInvoice.remaining_balance > 0 ||
            ((paymentsInvoice.total_amount || 0) - (paymentsInvoice.amount_paid || 0)) > 0) && (
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowRecordPayment(true)}
              sx={{ textTransform: 'none' }}
            >
              Record New Payment
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Record Payment Dialog (opened from Payments dialog) */}
      {paymentsInvoice && (
        <RecordPayment
          open={showRecordPayment}
          onClose={() => setShowRecordPayment(false)}
          invoice={paymentsInvoice}
          onPaymentRecorded={handlePaymentRecorded}
        />
      )}

      {/* Edit Payment Dialog (opened from Payments dialog) */}
      {editPaymentDialogOpen && paymentsInvoice && paymentToEdit && (
        <EditPayment
          open={editPaymentDialogOpen}
          onClose={() => {
            setEditPaymentDialogOpen(false);
            setPaymentToEdit(null);
          }}
          invoice={paymentsInvoice}
          payment={paymentToEdit}
          onPaymentUpdated={handlePaymentUpdated}
        />
      )}

      {/* Delete Payment Confirmation Dialog */}
      <Dialog
        open={deletePaymentConfirmOpen}
        onClose={() => {
          if (!isDeletingPayment) {
            setDeletePaymentConfirmOpen(false);
            setPaymentToDelete(null);
          }
        }}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete Payment?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            This will permanently remove the payment of{' '}
            <strong>${paymentToDelete?.amount?.toFixed(2) || '0.00'}</strong>
            {paymentToDelete?.payment_mode ? ` (${paymentToDelete.payment_mode})` : ''} from
            Invoice #{getInvoiceNumber(paymentsInvoice?.invoice_id)}.
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }} color="text.secondary">
            The invoice balance and the User Accounts Report will be updated accordingly. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => {
              setDeletePaymentConfirmOpen(false);
              setPaymentToDelete(null);
            }}
            disabled={isDeletingPayment}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleConfirmDeletePayment}
            disabled={isDeletingPayment}
            startIcon={isDeletingPayment ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            {isDeletingPayment ? 'Deleting...' : 'Delete Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Invoice Dialog */}
      {editInvoiceDialogOpen && invoiceToEdit && projectForEdit && (
        <EditInvoice
          open={editInvoiceDialogOpen}
          onClose={() => {
            setEditInvoiceDialogOpen(false);
            setInvoiceToEdit(null);
            setProjectForEdit(null);
          }}
          invoice={invoiceToEdit}
          project={projectForEdit}
          onInvoiceUpdated={handleInvoiceUpdated}
        />
      )}

      {/* Delete Invoice Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={handleCancelDelete}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Typography variant="h6" color="error">
            Delete Invoice Confirmation
          </Typography>
        </DialogTitle>
        <DialogContent>
          {deletionPreview && (
            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Are you sure you want to delete invoice <strong>{deletionPreview.invoiceId}</strong>?
              </Typography>
              <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1 }}>
                <Typography variant="body2" sx={{ color: '#664d03', fontWeight: 600 }} gutterBottom>
                  Warning: This action cannot be undone!
                </Typography>
                <Typography variant="body2" sx={{ color: '#664d03' }}>
                  Deleting this invoice will rollback all billing periods and invoiced quantities associated with it.
                </Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>Invoice Details:</Typography>
                <Typography variant="body2">Invoice Date: {new Date(deletionPreview.invoiceDate).toLocaleDateString()}</Typography>
                <Typography variant="body2">Total Amount: ${deletionPreview.totalAmount?.toFixed(2)}</Typography>
                <Typography variant="body2">Amount Paid: ${deletionPreview.amountPaid?.toFixed(2)}</Typography>
                <Typography variant="body2">Remaining Balance: ${deletionPreview.remainingBalance?.toFixed(2)}</Typography>
              </Box>
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>What will be affected:</Typography>
                <Typography variant="body2">Compartment billing periods to be removed: <strong>{deletionPreview.affectedChamberPeriods}</strong></Typography>
                <Typography variant="body2">Resource invoiced quantities to be removed: <strong>{deletionPreview.affectedResourceQuantities}</strong></Typography>
              </Box>
              {deletionPreview.hasPayments && (
                <Box sx={{ mt: 2, p: 2, bgcolor: '#fff3cd', border: '1px solid #ffc107', borderRadius: 1 }}>
                  <Typography variant="body2" sx={{ color: '#664d03' }}>
                    <strong>Warning:</strong> This invoice has recorded payments totaling ${deletionPreview.amountPaid?.toFixed(2)}.
                    The payments will be permanently deleted along with the invoice.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelDelete} disabled={isDeleting}>Cancel</Button>
          {deletionPreview && (
            <Button
              onClick={handleConfirmDelete}
              color="error"
              variant="contained"
              disabled={isDeleting}
              startIcon={isDeleting ? <CircularProgress size={20} /> : <DeleteIcon />}
            >
              {isDeleting ? 'Deleting...' : 'Delete Invoice'}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Account Mapping Dialog */}
      <Dialog
        open={accountMappingDialogOpen}
        onClose={() => setAccountMappingDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Map Invoice to Account Receivable
          {invoiceForMapping && (
            <Typography variant="body2" color="text.secondary">
              Invoice #{getInvoiceNumber(invoiceForMapping?.invoice_id)}
            </Typography>
          )}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Facility</InputLabel>
              <Select
                value={selectedMappingFacility}
                onChange={(e) => handleMappingFacilityChange(e.target.value)}
                label="Facility"
              >
                <MenuItem value="">
                  <em>Select a facility</em>
                </MenuItem>
                {facilities.map((facility) => (
                  <MenuItem key={facility._id} value={facility._id}>
                    {facility.facility_name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {selectedFacilityAccounts.length > 0 && (
              <FormControl fullWidth>
                <InputLabel>Account Receivable</InputLabel>
                <Select
                  value={selectedAccountNumber}
                  onChange={(e) => setSelectedAccountNumber(e.target.value)}
                  label="Account Receivable"
                >
                  <MenuItem value="">
                    <em>Select an account</em>
                  </MenuItem>
                  {selectedFacilityAccounts.map((account, index) => (
                    <MenuItem key={index} value={account.account_number}>
                      {account.account_number} {account.description && `- ${account.description}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            {selectedMappingFacility && selectedFacilityAccounts.length === 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
                No accounts receivable configured for this facility.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountMappingDialogOpen(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSaveAccountMapping}
            variant="contained"
            color="primary"
            disabled={!selectedMappingFacility || !selectedAccountNumber}
          >
            Save Mapping
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default ResourceInvoicing;
